// Color tokens represent the raw color values for each theme
// These should not be used directly in components

export const themeTokens = {
  colors: {
    light: {
      // Backgrounds
      background: '#E6E6E6', // Main app background - Used in root layout and Settings page F3F4F6
      surface: '#FFFFFF', // Primary surface color - Used in Prompt Cards, Sidebar, and Form backgrounds
      surfaceAlt: '#F9FAFB', // Secondary surface - Used in input fields, hover states, and secondary buttons
      transparentBackground: 'rgba(255, 255, 255, 0.1)', // Overlay backgrounds - Used in modals and dropdowns
      transparentBackgroundHover: 'rgba(255, 255, 255, 0.15)', // Hover state for overlays

      // Text Hierarchy
      textHeading: '#111827', // Primary heading color - Used in component titles, modal headers, settings sections
      textBody: '#111827', // Main content text - Used in form labels, menu items, and primary content
      textDescription: '#6B7280', // Secondary text - Used in help text, descriptions, and placeholder text
      textDisabled: '#9CA3AF', // Disabled state text - Used in inactive buttons, disabled inputs
      textLink: '#4F46E5', // Interactive links - Used in navigation, external links, and clickable text
      textError: '#DC2626', // Error messages - Used in form validation, error alerts

      // Text (for backward compatibility)
      text: '#111827', // Legacy primary text color - Maps to textBody in semantic colors
      textSecondary: '#1F2937', // Legacy secondary text - Used in older components
      textMuted: '#6B7280', // Legacy muted text - Used in older components

      // Brand
      brand: '#2563EB', // Primary brand color - Used in logos and primary brand elements
      brandHover: '#2563EB', // Brand hover state - Used for interactive brand elements
      brandMuted: '#818CF8', // Subdued brand color - Used for secondary brand elements

      // Accents
      accent: '#2563EB', // Primary accent - Used in focus states, selected items, and highlights
      accentHover: '#2563EB', // Accent hover - Used in interactive accent elements

      // Borders
      border: '#E5E7EB', // Primary border color - Used in cards, inputs, and dividers
      borderMuted: '#F3F4F6', // Subtle border color - Used in secondary dividers and subtle separators

      // Buttons
      buttonPrimary: '#3B82F6', // Primary action button background - Used in main CTAs and important actions
      buttonPrimaryHover: '#2563EB', // Primary button hover state
      buttonPrimaryText: '#FFFFFF', // Primary button text color
      buttonSecondary: '#799FF3', // Secondary button background - Used in less prominent actions
      buttonSecondaryHover: '#E5E7EB', // Secondary button hover state
      buttonSecondaryText: '#111827', // Secondary button text color
      buttonDisabled: '#9CA3AF', // Disabled button state - Used when actions are unavailable

      // Sidebar
      sidebarBackground: '#FFFFFF', // Main sidebar background
      sidebarBackgroundHover: '#F9FAFB', // Hover state for sidebar items
      sidebarText: '#111827', // Primary sidebar text - Used in folder names and labels
      sidebarTextHover: '#4B5563', // Hover state for sidebar text
      sidebarTextSelected: '#111827', // Selected item text color
      sidebarCollapseButton: '#4B5563', // Collapse/expand button color
      sidebarCollapseButtonHover: '#374151', // Collapse/expand button hover
      sidebarCollapseLabel: '#4B5563', // Collapse section label text
      sidebarCollapseLabelHover: '#374151', // Collapse section label hover
      sidebarWorkLabel: '#4B5563', // Workspace label text
      sidebarWorkLabelHover: '#374151', // Workspace label hover

      // Action Menu
      actionMenuBackground: 'rgba(0, 0, 0, 0.8)', // Dropdown menu background - Used in prompt card actions
      actionMenuIconHover: '#FFFFFF', // Icon hover state in action menu
      actionMenuUseHover: '#29B8FF', // Use/copy action hover state
      actionMenuOpenHover: '#7ED321', // Open in ChatGPT action hover state
      actionMenuEditHover: '#F5A623', // Edit action hover state
      actionMenuDeleteHover: '#D0021B', // Delete action hover state - Used for destructive actions

      // Settings
      settingsText: '#111827', // Primary text in settings panels
      settingsTextSecondary: '#4B5563', // Secondary text in settings - Used in descriptions
      settingsLabelText: '#374151', // Settings form labels and section headers
      settingsToggleEnabled: '#3B82F6', // Toggle button when enabled
      settingsToggleDisabled: '#E5E7EB', // Toggle button when disabled
      settingsToggleBorder: '#D1D5DB', // Toggle button border for visibility
      settingsToggleRing: '#E5E7EB', // Toggle button focus/disabled ring
      toggleEnabled: '#3B82F6', // Toggle button when enabled
      toggleDisabled: '#E5E7EB', // Toggle button when disabled
      toggleBorder: '#D1D5DB', // Toggle button border for visibility
      toggleRing: '#E5E7EB', // Toggle button focus/disabled ring

      // Sort Menu
      sortMenuBackground: '#FFFFFF', // Sort dropdown background
      sortMenuHoverBackground: '#F3F4F6', // Hover state for sort menu items
      sortMenuActiveBackground: '#EEF2FF', // Selected sort option background
      sortMenuText: '#111827', // Sort menu item text
      sortMenuHoverText: '#4F46E5', // Sort menu item hover text
      sortMenuDivider: '#E5E7EB', // Divider between sort menu sections
      sortMenuIconColor: '#6B7280', // Sort menu icons - Used in sort indicators
    },

    dark: {
      // Backgrounds
      background: '#111827', // Main app background - Used in root layout, settings page, and main content areas
      surface: '#111827', // Primary surface - Used in selected sidebar folders, new prompt form, and modal backgrounds
      surfaceAlt: '#374151', // Secondary surface - Used in input fields, hover states, and secondary UI elements
      transparentBackground: 'rgba(31, 41, 55, 0.2)', // Overlay backgrounds - Used for modals and dropdowns
      transparentBackgroundHover: 'rgba(31, 41, 55, 0.25)', // Hover state for overlays

      // Text Hierarchy
      textHeading: '#F9FAFB', // Primary heading text - Used in component titles, modal headers, settings sections
      textBody: '#F9FAFB', // Main content text - Used in form labels, menu items, and primary content
      textDescription: '#9CA3AF', // Secondary text - Used in help text, descriptions, and placeholder text
      textDisabled: '#6B7280', // Disabled text - Used in inactive buttons, disabled inputs, and unavailable options
      textLink: '#818CF8', // Interactive links - Used in navigation, external links, and clickable text
      textError: '#EF4444', // Error messages - Used in form validation, error alerts, and error states

      // Text (for backward compatibility)
      text: '#F9FAFB', // Legacy primary text - Maps to textBody in semantic colors
      textSecondary: '#9CA3AF', // Legacy secondary text - Used in older components
      textMuted: '#6B7280', // Legacy muted text - Used in older components

      // Brand
      brand: '#F9FAFB', // Primary brand elements - Used in logos and primary brand areas
      brandHover: '#9CA3AF', // Brand hover state - Used for interactive brand elements
      brandMuted: '#6B7280', // Subdued brand color - Used for secondary brand elements

      // Accents
      accent: '#6366F1', // Primary accent - Used in focus states, selected items, and highlights
      accentHover: '#4F46E5', // Accent hover - Used in interactive accent elements

      // Borders
      border: '#374151', // Primary border color - Used in cards, inputs, dividers, and form elements
      borderMuted: '#1F2937', // Subtle border color - Used in secondary dividers and subtle separators

      // Buttons
      buttonPrimary: '#3B82F6', // Primary action button - Used for main CTAs and important actions
      buttonPrimaryHover: '#2563EB', // Primary button hover - Interactive state for primary buttons
      buttonPrimaryText: '#FFFFFF', // Primary button text - Ensures readability on primary button background
      buttonSecondary: '#374151', // Secondary button - Used for less prominent actions
      buttonSecondaryHover: '#4B5563', // Secondary button hover - Interactive state for secondary buttons
      buttonSecondaryText: '#F9FAFB', // Secondary button text - Used in settings header and form buttons
      buttonDisabled: '#6B7280', // Disabled button state - Used when actions are unavailable

      // Sidebar
      sidebarBackground: '#1F2937', // Main sidebar background - Primary navigation area
      sidebarBackgroundHover: '#374151', // Sidebar item hover - Interactive state for navigation items
      sidebarText: '#F9FAFB', // Primary sidebar text - Used in folder names and navigation labels
      sidebarTextHover: '#D1D5DB', // Sidebar text hover - Interactive state for text elements
      sidebarTextSelected: '#F9FAFB', // Selected item text - Currently active navigation item
      sidebarCollapseButton: '#9CA3AF', // Collapse controls - Expand/collapse section buttons
      sidebarCollapseButtonHover: '#D1D5DB', // Collapse button hover - Interactive state
      sidebarCollapseLabel: '#9CA3AF', // Section labels - Collapsible section headers
      sidebarCollapseLabelHover: '#D1D5DB', // Section label hover - Interactive state
      sidebarWorkLabel: '#9CA3AF', // Workspace labels - Workspace section headers
      sidebarWorkLabelHover: '#D1D5DB', // Workspace label hover - Interactive state

      // Action Menu
      actionMenuBackground: 'rgba(0, 0, 0, 0.9)', // Menu background - Used in prompt card action menu
      actionMenuIconHover: '#29B8FF', // Menu icon hover - Interactive state for menu icons
      actionMenuUseHover: '#29B8FF', // Use action hover - Copy to clipboard action
      actionMenuOpenHover: '#7ED321', // Open action hover - Open in ChatGPT action
      actionMenuEditHover: '#F5A623', // Edit action hover - Edit prompt action
      actionMenuDeleteHover: '#D0021B', // Delete action hover - Delete prompt action

      // Settings
      settingsText: '#F9FAFB', // Primary settings text - Main content in settings panels
      settingsTextSecondary: '#9CA3AF', // Secondary settings text - Used in descriptions and help text
      settingsLabelText: '#D1D5DB', // Settings labels - Form labels and section headers
      settingsToggleEnabled: '#3B82F6', // Toggle button when enabled
      settingsToggleDisabled: '#374151', // Toggle button when disabled
      settingsToggleBorder: '#4B5563', // Toggle button border for visibility
      settingsToggleRing: '#374151', // Toggle button focus/disabled ring
      toggleEnabled: '#3B82F6', // Toggle button when enabled
      toggleDisabled: '#374151', // Toggle button when disabled
      toggleBorder: '#4B5563', // Toggle button border for visibility
      toggleRing: '#374151', // Toggle button focus/disabled ring

      // Sort Menu
      sortMenuBackground: '#1F2937', // Sort dropdown background - Menu container
      sortMenuHoverBackground: '#374151', // Sort item hover - Interactive state for menu items
      sortMenuActiveBackground: '#312E81', // Selected sort option - Currently active sort method
      sortMenuText: '#F9FAFB', // Sort menu text - Menu item labels
      sortMenuHoverText: '#818CF8', // Sort text hover - Interactive state for text
      sortMenuDivider: '#374151', // Menu dividers - Separates menu sections
      sortMenuIconColor: '#9CA3AF', // Sort icons - Indicates sort direction and type
    },

    spotify: {
      // Backgrounds
      background: '#727272', // Main app background - Used in root layout and main content areas
      surface: '#282828', // Primary surface - Used in cards, sidebars, and form backgrounds
      surfaceAlt: '#181818', // Secondary surface - Used in hover states and secondary elements
      transparentBackground: 'rgba(40, 40, 40, 0.2)', // Overlay backgrounds - Used in modals
      transparentBackgroundHover: 'rgba(40, 40, 40, 0.25)', // Hover state for overlays

      // Text Hierarchy
      textHeading: '#FFFFFF', // Primary heading text - Used in component titles and headers
      textBody: '#FFFFFF', // Main content text - Used in form labels and primary content
      textDescription: '#B3B3B3', // Secondary text - Used in descriptions and help text
      textDisabled: '#727272', // Disabled text - Used in inactive states
      textLink: '#1DB954', // Interactive links - Used for clickable text and navigation
      textError: '#F15856', // Error messages - Used in form validation and alerts

      // Text (for backward compatibility)
      text: '#FFFFFF', // Legacy primary text - Maps to textBody in semantic colors
      textSecondary: '#B3B3B3', // Legacy secondary text - Used in older components
      textMuted: '#727272', // Legacy muted text - Used in older components

      // Brand
      brand: '#1DB954', // Primary brand color - Used in logos and key brand elements
      brandHover: '#1ed760', // Brand hover state - Used for interactive brand elements
      brandMuted: '#1DB954', // Subdued brand color - Used in secondary brand elements

      // Accents
      accent: '#1DB954', // Primary accent - Used in focus states and highlights
      accentHover: '#1ed760', // Accent hover - Used in interactive accent elements

      // Borders
      border: '#282828', // Primary border color - Used in cards and dividers
      borderMuted: '#181818', // Subtle border color - Used in secondary separators

      // Buttons
      buttonPrimary: '#1DB954', // Primary action button - Used for main CTAs
      buttonPrimaryHover: '#1ed760', // Primary button hover - Interactive state
      buttonPrimaryText: '#FFFFFF', // Primary button text - Ensures readability
      buttonSecondary: '#282828', // Secondary button - Used for less prominent actions
      buttonSecondaryHover: '#181818', // Secondary button hover - Interactive state
      buttonSecondaryText: '#FFFFFF', // Secondary button text - Maintains contrast
      buttonDisabled: '#727272', // Disabled button state - Used when unavailable

      // Sidebar
      sidebarBackground: '#282828', // Main sidebar background - Primary navigation area
      sidebarBackgroundHover: '#181818', // Sidebar item hover - Interactive state
      sidebarText: '#FFFFFF', // Primary sidebar text - Navigation labels
      sidebarTextHover: '#E4E4E4', // Sidebar text hover - Interactive state
      sidebarTextSelected: '#FFFFFF', // Selected item text - Active navigation
      sidebarCollapseButton: '#B3B3B3', // Collapse controls - Section toggles
      sidebarCollapseButtonHover: '#E4E4E4', // Collapse button hover - Interactive state
      sidebarCollapseLabel: '#B3B3B3', // Section labels - Collapsible headers
      sidebarCollapseLabelHover: '#E4E4E4', // Section label hover - Interactive state
      sidebarWorkLabel: '#B3B3B3', // Workspace labels - Section headers
      sidebarWorkLabelHover: '#E4E4E4', // Workspace label hover - Interactive state

      // Action Menu
      actionMenuBackground: 'rgba(20, 20, 20, 0.9)', // Menu background - Prompt card actions
      actionMenuIconHover: '#29B8FF', // Menu icon hover - Interactive icon states
      actionMenuUseHover: '#29B8FF', // Use action hover - Copy action state
      actionMenuOpenHover: '#7ED321', // Open action hover - ChatGPT action state
      actionMenuEditHover: '#F5A623', // Edit action hover - Edit action state
      actionMenuDeleteHover: '#D0021B', // Delete action hover - Destructive action state

      // Settings
      settingsText: '#FFFFFF', // Primary settings text - Main content text
      settingsTextSecondary: '#B3B3B3', // Secondary settings text - Descriptions
      settingsLabelText: '#E4E4E4', // Settings labels - Form and section labels
      settingsToggleEnabled: '#1DB954', // Toggle button when enabled
      settingsToggleDisabled: '#282828', // Toggle button when disabled
      settingsToggleBorder: '#404040', // Toggle button border for visibility
      settingsToggleRing: '#282828', // Toggle button focus/disabled ring
      toggleEnabled: '#1DB954', // Toggle button when enabled
      toggleDisabled: '#282828', // Toggle button when disabled
      toggleBorder: '#404040', // Toggle button border for visibility
      toggleRing: '#282828', // Toggle button focus/disabled ring

      // Sort Menu
      sortMenuBackground: '#282828', // Menu background - Dropdown container
      sortMenuHoverBackground: '#181818', // Menu item hover - Interactive state
      sortMenuActiveBackground: '#1DB954', // Selected option - Current sort method
      sortMenuText: '#FFFFFF', // Menu text - Option labels
      sortMenuHoverText: '#1DB954', // Menu text hover - Interactive text
      sortMenuDivider: '#181818', // Menu dividers - Section separators
      sortMenuIconColor: '#B3B3B3', // Menu icons - Sort indicators
    },
  },
} as const;

export type ThemeTokens = typeof themeTokens;
export type ThemeName = keyof typeof themeTokens.colors;
